import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartDefs } from "../charts/chartEffects";
import { ChartTooltip } from "../charts/ChartTooltip";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import type { GroupDailySpendSeriesResponse, MyGroupUsageResponse } from "../../api/organisationReporting";
import { getVendorColor } from "../charts/chartColors";
import { VendorIcon } from "../consumption/VendorIcon";
import { formatDate, formatMoney, formatWholeMoney } from "../consumption/formatters";
import type { GroupPace } from "./groupPace";
import styles from "./GroupCard.module.css";
import { Tooltip as HoverTooltip } from "../shared/Tooltip";

/**
 * A bar from zero to the allowance: solid for spend so far, hatched out to the projection, and a tick at
 * today's place in the month, so spending ahead of pace reads as the solid bar passing the tick.
 */
export function AllowanceMeter({ pace }: { pace: GroupPace }): React.JSX.Element {
  const { t } = useTranslation();
  const used = Math.min(100, pace.percentUsed ?? 0);
  const projected = Math.min(100, pace.percentProjected ?? used);
  const over = pace.status === "heading" || pace.status === "over";
  const rounded = Math.round(pace.percentUsed ?? 0);

  return (
    <div
      className={styles.track}
      role="meter"
      aria-label={t("myGroups.card.meterLabel")}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(100, rounded)}
      aria-valuetext={t("myGroups.card.meterValue", { percent: rounded })}
    >
      <div className={`${styles.projectedFill} ${over ? styles.projectedOver : ""}`} style={{ width: `${String(projected)}%` }} />
      <div className={styles.usedFill} style={{ width: `${String(used)}%` }} />
      <div className={styles.todayMarker} style={{ left: `${String(pace.percentElapsed)}%` }}>
        <HoverTooltip label={t("myGroups.card.today")}>
          <span className={styles.todayHit} />
        </HoverTooltip>
      </div>
    </div>
  );
}

/** How the group's spend divides between tools: one bar of shares, then each tool's spend and how many people use it. */
export function ToolSplit({ group, compact, stacked = false }: { group: MyGroupUsageResponse; compact: boolean; stacked?: boolean }): React.JSX.Element | null {
  const { t } = useTranslation();
  const vendors = group.vendors.filter((vendor) => (vendor.totalSpend ?? 0) > 0);
  const total = vendors.reduce((sum, vendor) => sum + (vendor.totalSpend ?? 0), 0);
  if (vendors.length === 0 || total <= 0) {
    return null;
  }

  return (
    <div className={styles.tools}>
      <div className={styles.splitBar} aria-hidden="true">
        {vendors.map((vendor, index) => (
          <span key={vendor.vendorId} style={{ width: `${String(((vendor.totalSpend ?? 0) / total) * 100)}%`, backgroundColor: getVendorColor(vendor.vendorId, index) }} />
        ))}
      </div>
      {stacked ? (
        <ul className={styles.toolStack}>
          {vendors.map((vendor, index) => {
            const share = Math.round(((vendor.totalSpend ?? 0) / total) * 100);
            const adoption = group.personCount > 0 ? (vendor.personCount / group.personCount) * 100 : 0;
            return (
              <li key={vendor.vendorId}>
                <div className={styles.toolStackHead}>
                  <span className={styles.swatch} style={{ background: getVendorColor(vendor.vendorId, index) }} aria-hidden="true" />
                  <VendorIcon vendorId={vendor.vendorId} className={styles.toolIcon} />
                  <span className={styles.toolName}>{vendor.vendorDisplayName}</span>
                  <span className={styles.toolAmount}>{formatMoney(vendor.totalSpend ?? 0, vendor.currency)}</span>
                </div>
                <div className={styles.adoption} aria-hidden="true">
                  <span style={{ width: `${String(adoption)}%`, backgroundColor: getVendorColor(vendor.vendorId, index) }} />
                </div>
                <p className={styles.toolStackNote}>{t("myGroups.card.toolStackNote", { share, count: vendor.personCount, total: group.personCount })}</p>
              </li>
            );
          })}
        </ul>
      ) : (
      <ul className={compact ? `${styles.toolList} ${styles.toolListCompact}` : styles.toolList}>
        {vendors.map((vendor, index) => {
          const share = Math.round(((vendor.totalSpend ?? 0) / total) * 100);
          return (
            <li key={vendor.vendorId}>
              <span className={styles.swatch} style={{ background: getVendorColor(vendor.vendorId, index) }} aria-hidden="true" />
              <VendorIcon vendorId={vendor.vendorId} className={styles.toolIcon} />
              <span className={styles.toolName}>{vendor.vendorDisplayName}</span>
              <span className={styles.toolAmount}>
                {compact ? `${String(share)}%` : `${formatMoney(vendor.totalSpend ?? 0, vendor.currency)} · ${String(share)}%`}
              </span>
              {!compact && <span className={styles.toolPeople}>{t("myGroups.card.toolPeople", { count: vendor.personCount, total: group.personCount })}</span>}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}

/** Each day's spend, stacked by tool, on one money scale: the detail behind the month line. */
export function DailyToolBars({ group, series }: { group: MyGroupUsageResponse; series: GroupDailySpendSeriesResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const effects = useChartEffectIds();
  const vendors = group.vendors.filter((vendor) => (vendor.totalSpend ?? 0) > 0);
  const colors = new Map(vendors.map((vendor, index) => [vendor.vendorId, getVendorColor(vendor.vendorId, index)]));
  const data = series.days.map((day) => {
    const point: Record<string, number | string> = { date: day.date };
    for (const vendor of vendors) {
      point[vendor.vendorId] = day.vendors.find((amount) => amount.vendorId === vendor.vendorId)?.amount ?? 0;
    }
    return point;
  });

  return (
    <div className={styles.daily}>
      <h3 className={`panel-title ${styles.dailyTitle}`}>{t("myGroups.card.dailyTitle")}</h3>
      <div className={styles.chart} style={{ height: 150 }} role="img" aria-label={t("myGroups.card.dailyTitle")}>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 4 }}>
            <ChartDefs ids={effects} gradients={vendors.map((vendor) => ({ key: vendor.vendorId, color: colors.get(vendor.vendorId) ?? "", top: 1, bottom: 0.55 }))} />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tickFormatter={(date: string) => formatDate(date)} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} minTickGap={32} />
            <YAxis axisLine={false} tickLine={false} tickFormatter={(value: number) => formatWholeMoney(value, group.currency)} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} width={64} />
            <Tooltip
              content={<ChartTooltip colors={colors} formatLabel={formatDate} formatValue={(value) => formatMoney(value, group.currency)} />}
              cursor={{ fill: "color-mix(in srgb, var(--color-chart-blue) 10%, transparent)" }}
            />
            {vendors.map((vendor, index) => (
              <Bar
                key={vendor.vendorId}
                dataKey={vendor.vendorId}
                name={vendor.vendorDisplayName}
                stackId="tools"
                fill={`url(#${effects.gradient(vendor.vendorId)})`}
                // Only the top of the stack is rounded; a rounded segment inside it would leave a notch.
                radius={index === vendors.length - 1 ? [3, 3, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
